/*
 * Copyright (c) 2020  Apricad LLC. All Rights Reserved.
 */

import * as React from "react";
import { useState } from "react";
import { Formik, Form, Field } from "formik";

import createStyles from "@material-ui/core/styles/createStyles";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";

// @material-ui/core
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@material-ui/core";
// @material-ui/icons
import Key from "@material-ui/icons/VpnKey";
import Lock from "@material-ui/icons/Lock";
import Mail from "@material-ui/icons/Mail";

import { forgotPassword, confirmPassword } from "./authCognito.jsx";
import {
  emailValidation,
  resetValidation,
  FormTextField,
  closeButton,
  btnFormSubmit,
  passwordEyeIcon,
} from "components/Form/FormField";
import { Modal } from "components/Modal/Modal";

import {
  sProceed,
  sResetPassword,
  sConfirmSendCode,
  sResetInfo,
  sResetOk,
  sResetOkMsg,
  sResetFail,
  sResetInfoFail,
  sResetFailMsg,
  sCodeMatchFail,
  sSignUpFail,
  sErrTwoPW,
} from "assets/jss/views/ResStrings";
import { showHtmlText } from "client/views/Exercise/ExMain";

import accountPageStyle from "assets/jss/views/accountPage/accountPageStyle.jsx";
const styles = createStyles(accountPageStyle as any);

interface FormValues {
  email: string;
  code: string;
  newPass1: string;
  newPass2: string;
}

interface Props extends WithStyles<typeof styles> {
  onExit: (email?: string) => void;
}

const ResetDialogBase: React.FC<Props> = ({ onExit, classes }: Props) => {
  const [phase, setPhase] = useState(1); // phase 1: ask email; phase 2: ask code and new password
  const [showPassword, setShowPassword] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertModal, setAlertModal] = useState<React.ReactElement | null>(null);

  const showMessage = (
    title: string,
    body: string,
    warning: boolean = false,
    func: () => void = () => {},
  ) => {
    setAlertModal(
      <Modal
        title={title}
        color={warning ? "warning" : "secondary"}
        onExit={() => {
          setShowAlert(false);
          func && func();
        }}>
        {body}
      </Modal>,
    );
    setShowAlert(true);
  };

  const resetPassword = async (email: string) => {
    var res = await forgotPassword(email);

    if (res.result) {
      // Cognito has sent the code
      setPhase(2);
    } else {
      showMessage(
        sResetFail,
        res.err.code === "UserNotFoundException"
          ? sResetInfoFail
          : sResetFailMsg,
        true,
      );
    }
  };

  // Password Reset
  const confirmReset = async (
    email: string,
    code: string,
    password: string,
  ) => {
    var res = await confirmPassword(email, code, password);

    if (res.result) {
      // tell user the code and new password are good, bring them to Login Dialog
      showMessage(
        sResetOk,
        sResetOkMsg,
        false,
        () => onExit(email), // Send back email to LoginDialog
      );
    } else {
      // password reset failed, for code mismatch or other reason
      const codeMismatch =
        res.err.code === "CodeMismatchException" ? true : false;
      showMessage(
        sResetFail,
        codeMismatch ? sCodeMatchFail : sResetFailMsg,
        true,
        () => {
          // if not code mismatch (which allows retry), close the Reset Dialog
          if (!codeMismatch) {
            closeDlg();
          }
        },
      );
    }
  };

  const handleSubmit = (values: FormValues) => {
    const { email, code, newPass1, newPass2 } = values;
    if (phase === 1) {
      resetPassword(email);
    } else {
      if (newPass1 !== newPass2) {
        showMessage(sSignUpFail, sErrTwoPW, true);
      } else {
        confirmReset(email, code, newPass1);
      }
    }
  };

  const renderPasswordField = (valueName: string, labelPrefix: string) => (
    <Field
      name={valueName}
      type={showPassword ? "text" : "password"}
      icon={<Lock />}
      component={FormTextField}
      label={labelPrefix + "New Password"}
      eye={passwordEyeIcon(showPassword, () => setShowPassword(!showPassword))}
    />
  );

  const renderInput = () =>
    phase === 1 ? (
      <Field
        name="email"
        type="text"
        icon={<Mail />}
        component={FormTextField}
        label="Email"
      />
    ) : (
      <>
        <Field
          name="email"
          type="text"
          disabled
          icon={<Mail />}
          component={FormTextField}
          label="Email"
        />
        <Field
          name="code"
          type="text"
          icon={<Key />}
          component={FormTextField}
          label="Verification Code"
        />

        {renderPasswordField("newPass1", "")}
        {renderPasswordField("newPass2", "Retype ")}
      </>
    );

  const closeDlg = () => {
    setPhase(1);
    onExit();
  };

  return (
    <>
      {showAlert && alertModal}
      <Dialog
        classes={{ paper: classes.alert }}
        open={true}
        disableBackdropClick={phase === 1 ? false : true}
        onClose={closeDlg}>
        <DialogTitle className={classes.alertNormal} disableTypography>
          <h4>{sResetPassword}</h4>
          {closeButton(classes, closeDlg)}
        </DialogTitle>
        <div className={classes.alertBody}>
          <DialogContent className={classes.modalBody}>
            <Formik
              initialValues={{
                email: "",
                code: "",
                newPass1: "",
                newPass2: "",
              }}
              onSubmit={(values) => handleSubmit(values)}
              validationSchema={phase === 1 ? emailValidation : resetValidation}
              render={({ isValid }) => (
                <Form>
                  <p>
                    {phase === 1 ? sConfirmSendCode : showHtmlText(sResetInfo)}
                  </p>
                  {renderInput()}
                  <DialogActions
                    className={`${classes.modalFooter} ${classes.justifyContentCenter}`}>
                    {btnFormSubmit({ label: sProceed, disabled: !isValid })}
                  </DialogActions>
                </Form>
              )}
            />
          </DialogContent>
        </div>
      </Dialog>
    </>
  );
};

const base: any = ResetDialogBase;
export const ResetDialog = withStyles(styles)(base);

export const ResetDialogTest = withStyles(styles)(base);
